import { type KeyboardEvent, useEffect, useId, useRef, useState } from 'react'
import { CircleHelp } from 'lucide-react'

// One example per kind of requirement shoppers can type.
export const SEARCH_TIPS = [
  { kind: 'Size', query: 'undercounter ice maker under 25 inches wide' },
  { kind: 'Fuel', query: 'round gas pizza oven' },
  { kind: 'Budget', query: 'reach-in freezer under $3,000' },
  { kind: 'Ship time', query: 'countertop griddle that ships within a week' },
] as const

export function SearchTips({
  onPick,
  onOpen,
  className,
}: {
  onPick: (query: string) => void
  onOpen?: () => void
  className?: string
}) {
  const id = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const firstRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!open) return
    firstRef.current?.focus()
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  const toggle = () => {
    if (!open) onOpen?.()
    setOpen(!open)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape') return
    event.stopPropagation()
    setOpen(false)
    triggerRef.current?.focus()
  }

  return (
    <div
      ref={rootRef}
      className={`sf-tips ${className ?? ''}`}
      onKeyDown={onKeyDown}
      onBlur={(event) => {
        if (!rootRef.current?.contains(event.relatedTarget as Node | null)) setOpen(false)
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        className="sf-tips__trigger"
        aria-label="Search tips"
        aria-expanded={open}
        aria-controls={open ? `${id}-tips` : undefined}
        aria-haspopup="dialog"
        data-e2e="search-tips"
        onClick={toggle}
      >
        <CircleHelp size={18} strokeWidth={1.75} aria-hidden="true" />
      </button>
      {open ? (
        <div
          id={`${id}-tips`}
          role="dialog"
          aria-labelledby={`${id}-heading`}
          className="sf-tips__panel"
        >
          <p id={`${id}-heading`} className="sf-tips__heading">
            Describe what you need
          </p>
          <ul className="sf-tips__list">
            {SEARCH_TIPS.map((tip, index) => (
              <li key={tip.kind}>
                <button
                  ref={index === 0 ? firstRef : undefined}
                  type="button"
                  className="sf-tips__example"
                  data-e2e="search-tip"
                  onClick={() => {
                    setOpen(false)
                    onPick(tip.query)
                  }}
                >
                  <span className="sf-tips__kind">{tip.kind}</span>{' '}
                  <span className="sf-tips__query">{tip.query}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )
}
